import * as z from "zod";

/** Fleet management tools. Only list_fleet is read-only. */
export const FLEET_TOOL_NAMES = [
  "list_fleet",
  "create_group",
  "update_group",
  "organize_bot",
  "set_child_instructions",
  "set_child_bot_model",
  "create_child_routine",
] as const;

export const FLEET_WRITE_TOOL_NAMES = FLEET_TOOL_NAMES.filter(
  (name): name is Exclude<(typeof FLEET_TOOL_NAMES)[number], "list_fleet"> => name !== "list_fleet",
);

/** Built-in tool names a bot may disable. Kept aligned with the adapter tool list. */
export const BUILTIN_TOOL_NAMES = [
  "connect_devices",
  "connect_execute",
  "connect_result",
  "connect_cancel",
  "site_schema",
  "site_list",
  "site_create",
  "site_update",
  "site_versions",
  "site_source",
  "site_import_media",
  "site_publish",
  "computer_observe",
  "computer_act",
  "browser_navigate",
  "browser_snapshot",
  "browser_act",
  "list_files",
  "read_file",
  "write_file",
  "attach_file",
  "attach_artifact",
  "list_artifacts",
  "inspect_image",
  "get_artifact",
  "shell",
  "open_path",
  "launch_app",
  "request_takeover",
  "ask_user",
  "ask_text",
  "ask_file",
  "connect_app",
  "message_user",
  "request_secret",
  "list_secrets",
  "secret_request",
  "forget_secret",
  "render_plot",
  "generate_image",
  "generate_video",
  "add_mcp_server",
  "remember",
  "save_shared_memory",
  "web_search",
  "web_fetch",
  "cloud_agent_launch",
  "cloud_agent_status",
  "cloud_agent_reply",
  "cloud_agent_cancel",
  "search_history",
  "read_history",
  "save_memory",
  "recall_memory",
  "forget_memory",
  "task_catalog",
  "delegation_list",
  "delegation_update",
  "scratchpad_list",
  "scratchpad_add",
  "scratchpad_update",
  "scratchpad_complete",
  "scratchpad_remove",
  "board_tickets",
  "ticket_get",
  "ticket_create",
  "ticket_update",
  "ticket_comment",
  "schedule_create",
  "schedule_list",
  "schedule_cancel",
  "end_call",
  "call_user",
  "skill_read",
  "skill_create",
  "skill_update",
  "skill_delete",
  "run_subagent",
  "propose_instructions_update",
  "create_space",
  "spawn_bot",
  "update_bot",
  ...FLEET_TOOL_NAMES,
  "archive_bot",
  "message_bot",
  "handoff_to_bot",
  "connect_agent",
  "respond_agent_connection",
  "message_agent",
] as const;

/** Read-only research, never authority to execute a skill's external actions.
 * Дослідження лише для читання не дозволяє виконувати зовнішні дії навички. */
export const COORDINATOR_RESEARCH_TOOL_NAMES = [
  "web_search",
  "web_fetch",
  "skill_read",
  "task_catalog",
  "recall_memory",
  "search_history",
  "read_history",
] as const;

export const COORDINATOR_TOOL_NAMES = [
  "list_files",
  "read_file",
  "write_file",
  "attach_file",
  "get_artifact",
  "shell",
  "computer_observe",
  "computer_act",
  "browser_navigate",
  "browser_snapshot",
  "browser_act",
  "open_path",
  "launch_app",
  "request_takeover",
  "message_user",
  "ask_user",
  "ask_text",
  "ask_file",
  "remember",
  "save_shared_memory",
  "save_memory",
  "forget_memory",
  ...COORDINATOR_RESEARCH_TOOL_NAMES,
  "run_subagent",
  "scratchpad_list",
  "delegation_list",
  "delegation_update",
  "scratchpad_add",
  "scratchpad_update",
  "scratchpad_complete",
  "scratchpad_remove",
  "board_tickets",
  "ticket_get",
  "ticket_create",
  "ticket_update",
  "ticket_comment",
  "schedule_create",
  "schedule_list",
  "schedule_cancel",
  "end_call",
  "call_user",
  "list_artifacts",
  "inspect_image",
  // Deliver a saved file without a computer. / Передати збережений файл без комп'ютера.
  "attach_artifact",
  "propose_instructions_update",
  "spawn_bot",
  "update_bot",
  "archive_bot",
  ...FLEET_TOOL_NAMES,
  "message_bot",
  "handoff_to_bot",
] as const satisfies readonly (typeof BUILTIN_TOOL_NAMES)[number][];

const coordinatorToolSet: ReadonlySet<string> = new Set(COORDINATOR_TOOL_NAMES);

/** Every built-in tool a coordinator does not keep. */
export function coordinatorDisabledBuiltinTools(): string[] {
  return BUILTIN_TOOL_NAMES.filter((name) => !coordinatorToolSet.has(name));
}

export const BuiltinToolNameSchema = z.enum(BUILTIN_TOOL_NAMES);
export type BuiltinToolName = z.infer<typeof BuiltinToolNameSchema>;

const builtinToolNameSet: ReadonlySet<string> = new Set(BUILTIN_TOOL_NAMES);

export function isBuiltinToolName(name: string): name is BuiltinToolName {
  return builtinToolNameSet.has(name);
}

export function disabledBuiltinToolSet(names: readonly string[] | undefined): Set<string> {
  const disabled = new Set<string>();
  if (!names) return disabled;
  for (const raw of names) {
    const name = raw.trim();
    if (isBuiltinToolName(name)) disabled.add(name);
  }
  return disabled;
}

export const DisabledBuiltinToolsSchema = z
  .array(z.string().trim().min(1).max(64))
  .max(BUILTIN_TOOL_NAMES.length)
  .superRefine((names, ctx) => {
    names.forEach((name, index) => {
      if (!isBuiltinToolName(name)) {
        ctx.addIssue({
          code: "custom",
          message: `Unknown built-in tool: ${name}`,
          path: [index],
        });
      }
    });
  })
  .transform((names) => [...new Set(names)]);
